import { inlineMarkdownToText } from '@/lib/answerLineText'
// The Extractions in a pack's research document, one block each (busy-my-web#575).
//
// A saved extraction's `title` is usually just the clipped start of its own text ("Green Tea: Rich
// in polyphenols such as EGCG, with powerful a…"), and the document used to write it above the
// full text as an <h4>. The editor only knows h1–h3, so that <h4> came back as a plain paragraph:
// every extraction read twice — a line cut mid-word, then the text — and four of them ran together
// as one wall of paragraphs. The anchor lookup matched the clipped line first, so the timeline's
// highlight framed that one line. Documents are saved and are the user's, so the cure is applied
// whenever a document enters the editor, not only when a new one is written.

// Comparison form: case-, whitespace-, dash- and markdown-emphasis-insensitive. Whitespace goes
// entirely because a snippet's newlines are written as <br>, which has no text — "a\nb" reads "ab"
// in the document. Emphasis marks go because a saved text is often markdown ("_No language model
// …_") while its clipped title is not, and that one underscore kept the duplicate line alive
// (Pouria, 2026-10-01).
export const canon = (t) => String(t || '')
  .toLowerCase()
  .replace(/[_*`~]/g, '')
  .replace(/[‒-―−]/g, '-')
  .replace(/[‘’]/g, "'")
  .replace(/[“”]/g, '"')
  .replace(/\s+/g, '')

const unclip = (t) => String(t || '').trim().replace(/(…|\.{3})$/, '').trim()

/** An extraction's REAL title, or '' — a title that is only the clipped start of the text (or the
 *  text itself) is not a title, it is the duplicate line #575 removed. */
export function extractionTitle(r) {
  const bare = unclip(r?.title)
  if (!bare) return ''
  return canon(r?.content).startsWith(canon(bare)) ? '' : String(r.title).trim()
}

// The words a heading is made of. Markdown links read as their words and "[5]" citation markers go
// (Pouria, 2026-10-01): items saved before the answer line was cleaned (lib/answerLineText.js) carry
// "[gearpatrol.com](https://…) [5]" in their text, and the heading was built out of that raw.
const plain = (t) => inlineMarkdownToText(t).replace(/[_*`~]+/g, '').replace(/\s+/g, ' ').trim()
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// The text up to its first clause break (. ! ? : ; , — – before a space or the end), skipping any
// break inside brackets or quotes — "Herbal Teas (Ginger, Chamomile): …" is "Herbal Teas (Ginger,
// Chamomile)", never "Herbal Teas (Ginger".
const OPEN = '([{“‘', CLOSE = ')]}”’'
function firstClause(text) {
  let depth = 0
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (OPEN.includes(c)) depth++
    else if (CLOSE.includes(c)) depth = Math.max(0, depth - 1)
    else if (!depth && '.!?:;,—–'.includes(c) && (i === text.length - 1 || /\s/.test(text[i + 1]))) return text.slice(0, i).trim()
  }
  return text.trim()
}

/**
 * The heading every extraction wears in the document (Pouria, 2026-10-01: "when we try to show the
 * extraction we can create title for it"). Its real title when it has one; otherwise a title made
 * from its own words — the first clause, at most eight words, never cut mid-word and never ending
 * in "…", so it reads as a heading and not as the clipped duplicate #575 removed. '' when the whole
 * text is that short already — then the text is its own title.
 */
export function extractionHeading(r) {
  const real = extractionTitle(r)
  if (real) return plain(real)
  const text = plain(r?.content || r?.title).replace(/(…|\.{3})$/, '').trim()
  if (!text) return ''
  const clause = firstClause(text)
  let head = clause.split(' ').length >= 3 ? clause : text
  const words = head.split(' ')
  if (words.length > 8) head = words.slice(0, 8).join(' ')
  head = head.replace(/[\s,.;:—–-]+$/, '')
  if (!head || canon(head) === canon(text)) return ''
  return head.charAt(0).toUpperCase() + head.slice(1)
}

// An extraction's text as document html: escaped, a text wrapped whole in markdown emphasis drawn
// as italics rather than with its underscores showing, newlines as <br>.
const bodyHtml = (content) => {
  let t = esc(String(content || '').trim())
  const m = t.match(/^([_*])([\s\S]+)\1$/)
  if (m) t = `<em>${m[2].trim()}</em>`
  return t.replace(/\n/g, '<br>')
}

const blockInner = (r) => {
  const head = extractionHeading(r)
  return head ? `<strong>${esc(head)}</strong><br>${bodyHtml(r?.content)}` : `<strong>${bodyHtml(r?.content)}</strong>`
}

/** One extraction as ONE document block — its heading in bold over its text, in a single
 *  paragraph, so the timeline's highlight frames the whole extraction and not one line of it. */
export const extractionBlockHtml = (r) => `<p>${blockInner(r)}</p>`

/**
 * `html` with each of `notes` as ONE block headed by its title (`extractionHeading`): the clipped
 * duplicate line or real title above an extraction's text is dropped and the heading goes into the
 * text's own paragraph as bold, so the extraction is a single separated block and the anchor frames
 * all of it. A text the user has edited keeps its words; everything else in the document is left
 * exactly as it is. Idempotent; returns `html` untouched when there is nothing to do.
 */
export function tidyPackExtractions(html, notes) {
  if (!html || !(notes || []).length) return html
  const keys = notes
    .map(n => ({ n, key: canon(n?.content).slice(0, 60), full: canon(n?.content), title: canon(extractionTitle(n)) }))
    .filter(k => k.key.length >= 12)
  if (!keys.length) return html
  const div = document.createElement('div')
  div.innerHTML = html
  let changed = false
  for (const body of [...div.querySelectorAll('p')]) {
    // Already headed — the bold title leads the block. What makes this idempotent.
    if (body.firstChild && body.firstChild.nodeName === 'STRONG') continue
    const bodyText = canon(body.textContent)
    const note = keys.find(k => bodyText.startsWith(k.key))
    if (!note) continue
    // The line above it, when it is only the clipped start of this text or its real title, goes:
    // the heading carries that now.
    const prev = body.previousElementSibling
    if (prev && ['P', 'H4'].includes(prev.tagName) && !prev.querySelector('img')) {
      const prevText = canon(unclip(prev.textContent))
      if (prevText && ((prevText.length < bodyText.length && bodyText.startsWith(prevText)) || (note.title && prevText === note.title))) prev.remove()
    }
    if (bodyText === note.full && !body.querySelector(':not(br)')) {
      // Untouched by the user — rebuilt whole, so its emphasis marks become italics too.
      body.innerHTML = blockInner(note.n)
    } else {
      // Edited — the user's words stay exactly as they are; only the heading is added above them.
      const head = extractionHeading(note.n)
      if (!head) continue
      const strong = document.createElement('strong')
      strong.textContent = head
      body.prepend(strong, document.createElement('br'))
    }
    changed = true
  }
  return changed ? div.innerHTML : html
}
